// Dragging sections into and around the email, with a mouse or a finger.
// Uses pointer events (phones don't do browser drag-and-drop). Photo files dragged in from the computer
// use normal browser drag-and-drop instead; Canvas handles those.
//
// Mouse: the drag starts after the pointer moves a few pixels, so a plain click still works.
// Touch: press and hold first (unless the item has no hold, like the grip), so the list still scrolls.

import type { BlockType } from '../../shared/blocks';

export type DragPayload = { kind: 'add'; type: BlockType; label: string } | { kind: 'move'; id: string; label: string };

export type DropAction =
  | { kind: 'add'; type: BlockType; index: number }
  | { kind: 'move'; id: string; index: number }
  | { kind: 'files'; files: File[]; index: number }
  | { kind: 'replace'; file: File; id: string; path: string };

/** The email canvas registers itself here. Coordinates are in the main window. */
export interface DropTarget {
  frame(): HTMLIFrameElement | null;
  scroller(): HTMLElement | null;
  over(x: number, y: number, p: DragPayload): void;
  end(x: number, y: number, p: DragPayload, commit: boolean): void;
}

let target: DropTarget | null = null;
export function setDropTarget(t: DropTarget | null): void {
  target = t;
}

const MOVE_START = 5; // px a mouse moves before the drag starts
const HOLD_SLOP = 10; // px a finger can wander while holding

interface Opts {
  /** ms a finger must hold before the drag starts. 0 = start on first move. */
  hold: number;
  onStart?: () => void;
}

let active = false;
export const isDragging = () => active;

/** Call from pointerdown on anything draggable. */
export function armDrag(e: PointerEvent, payload: DragPayload, opts: Opts): void {
  if (active || !target) return;
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  const touch = e.pointerType !== 'mouse';
  const el = e.currentTarget instanceof Element ? e.currentTarget : (e.target as Element);
  const startWin = el.ownerDocument.defaultView!;
  const frame = target.frame();
  const frameWin = frame?.contentWindow ?? null;

  // Pointer position in main-window coordinates, wherever the event came from.
  const toMain = (ev: PointerEvent | MouseEvent): [number, number] => {
    const w = (ev.target as Node | null)?.ownerDocument?.defaultView ?? (ev.view as Window | null);
    if (w && frame && w === frameWin) {
      const r = frame.getBoundingClientRect();
      return [ev.clientX + r.left, ev.clientY + r.top];
    }
    return [ev.clientX, ev.clientY];
  };

  const [sx, sy] = toMain(e);
  let x = sx;
  let y = sy;
  let started = false;
  let holdTimer: number | undefined;
  let ghost: HTMLElement | null = null;
  let raf = 0;

  try {
    el.setPointerCapture(e.pointerId);
  } catch {
    /* some elements can't capture; the window listeners still cover it */
  }

  const begin = () => {
    if (started) return;
    started = true;
    active = true;
    clearTimeout(holdTimer);
    navigator.vibrate?.(12);
    document.documentElement.classList.add('is-dragging');
    ghost = document.createElement('div');
    ghost.className = 'drag-ghost';
    ghost.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.6"/><circle cx="15" cy="6" r="1.6"/><circle cx="9" cy="12" r="1.6"/><circle cx="15" cy="12" r="1.6"/><circle cx="9" cy="18" r="1.6"/><circle cx="15" cy="18" r="1.6"/></svg>`;
    ghost.append(payload.label);
    document.body.appendChild(ghost);
    opts.onStart?.();
    place();
    raf = requestAnimationFrame(edgeScroll);
  };

  const place = () => {
    // A finger covers what's under it, so on touch the label rides above the finger.
    if (ghost) ghost.style.transform = touch ? `translate(${x - 30}px, ${y - 64}px)` : `translate(${x + 14}px, ${y + 14}px)`;
    target?.over(x, y, payload);
  };

  // Near the top or bottom of the email, scroll it.
  const edgeScroll = () => {
    if (!started) return;
    const sc = target?.scroller();
    if (sc) {
      const r = sc.getBoundingClientRect();
      const zone = 70;
      let dy = 0;
      if (y < r.top + zone) dy = -Math.ceil(((r.top + zone - y) / zone) * 14);
      else if (y > r.bottom - zone) dy = Math.ceil(((y - (r.bottom - zone)) / zone) * 14);
      if (dy) {
        const before = sc.scrollTop;
        sc.scrollTop += dy;
        if (sc.scrollTop !== before) target?.over(x, y, payload);
      }
    }
    raf = requestAnimationFrame(edgeScroll);
  };

  const move = (ev: PointerEvent) => {
    if (ev.pointerId !== e.pointerId) return;
    [x, y] = toMain(ev);
    const dist = Math.hypot(x - sx, y - sy);
    if (!started) {
      if (touch && opts.hold > 0) {
        if (dist > HOLD_SLOP) finish(false); // it's a scroll, not a hold
        return;
      }
      if (dist < (touch ? 2 : MOVE_START)) return;
      begin();
    }
    ev.preventDefault();
    place();
  };

  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== e.pointerId) return;
    [x, y] = toMain(ev);
    finish(started);
  };
  const cancel = (ev: PointerEvent) => {
    if (ev.pointerId === e.pointerId) finish(false);
  };
  const key = (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') finish(false);
  };
  // Once dragging, a finger must not scroll the page.
  const blockTouch = (ev: TouchEvent) => {
    if (started) ev.preventDefault();
  };
  const blockMenu = (ev: Event) => ev.preventDefault();

  const wins = [...new Set([startWin, window, frameWin].filter(Boolean) as Window[])];
  for (const w of wins) {
    w.addEventListener('pointermove', move, { passive: false });
    w.addEventListener('pointerup', up);
    w.addEventListener('pointercancel', cancel);
    w.addEventListener('keydown', key);
    w.addEventListener('touchmove', blockTouch, { passive: false });
    w.addEventListener('contextmenu', blockMenu);
  }

  if (touch && opts.hold > 0) holdTimer = window.setTimeout(begin, opts.hold);

  function finish(commit: boolean) {
    clearTimeout(holdTimer);
    cancelAnimationFrame(raf);
    for (const w of wins) {
      w.removeEventListener('pointermove', move);
      w.removeEventListener('pointerup', up);
      w.removeEventListener('pointercancel', cancel);
      w.removeEventListener('keydown', key);
      w.removeEventListener('touchmove', blockTouch);
      w.removeEventListener('contextmenu', blockMenu);
    }
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    if (!started) return;
    ghost?.remove();
    document.documentElement.classList.remove('is-dragging');
    active = false;
    started = false;
    target?.end(x, y, payload, commit);
    // The click that follows a drag must not also add or select something.
    const eat = (ev: Event) => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    for (const w of wins) w.addEventListener('click', eat, true);
    setTimeout(() => wins.forEach((w) => w.removeEventListener('click', eat, true)), 60);
  }
}

